import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  markAllNotificationsAsSeen,
  markNotificationAsSeen,
} from '../../../api/notifications.api';
import { reportError } from '../../../helpers/reportError';
import { useSafeWindowOpen } from '../../../hooks/useSafeWindowOpen';
import { useUIState } from '../../../store/auth';
import type { NotificationEntity } from '../../../types/notification.types';
import { notificationTarget } from '../helpers/notificationTarget';
import {
  getUnreadCount,
  markSeenInCache,
  refreshNotifications,
  setUnreadCount,
} from '../notificationsCache';
import { useNotificationsViewer } from './useNotificationsViewer';

/**
 * Opening a notification, and marking them all read.
 *
 * Both are optimistic: the row turns read and the badge drops on the click,
 * and the count the server answers with then overwrites the guess. The
 * `notifications_read` echo of the same action finds the count already right
 * and does nothing (see `useNotificationsSocket`).
 */
export const useNotificationActions = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userId, role } = useNotificationsViewer();
  const openWindow = useSafeWindowOpen();
  const [markingAll, setMarkingAll] = useState(false);

  /**
   * Mark it read, then go where it points. Answers whether it moved to a page
   * of this app, which a tapped push needs to know (see `useOpenFromPush`);
   * `replace` is for that case too, so Back skips the hop through the list.
   *
   * The navigation does not wait for the request: a read flag is not worth a
   * spinner between the click and the page, and a failure only means the row
   * comes back unread on the next fetch - which the catch asks for.
   */
  const open = useCallback(
    (
      notification: NotificationEntity,
      { replace = false }: { replace?: boolean } = {}
    ) => {
      if (!notification.seen) {
        markSeenInCache(queryClient, userId, notification.id);
        const before = getUnreadCount(queryClient, userId);
        if (before !== undefined) {
          setUnreadCount(queryClient, userId, before - 1);
        }
        markNotificationAsSeen(notification.id)
          .then(({ data }) =>
            setUnreadCount(queryClient, userId, data.data.unread)
          )
          .catch((error) => {
            reportError(error, { silent: true, context: 'markNotification' });
            refreshNotifications(queryClient, userId);
          });
      }

      const target = notificationTarget(notification, role);
      if (!target) return false;
      if (target.external) {
        // Still inside the click, so the popup blocker lets it through.
        openWindow(target.to, { immediate: true });
        return false;
      }
      if (target.uiState) useUIState.setState(target.uiState);
      navigate(target.to, { state: target.state, replace });
      return true;
    },
    [queryClient, userId, role, navigate, openWindow]
  );

  const markAll = useCallback(async () => {
    if (markingAll) return;
    setMarkingAll(true);
    markSeenInCache(queryClient, userId, null);
    setUnreadCount(queryClient, userId, 0);
    try {
      const { data } = await markAllNotificationsAsSeen();
      setUnreadCount(queryClient, userId, data.data.unread);
    } catch (error) {
      reportError(error, { context: 'markAllNotifications' });
      refreshNotifications(queryClient, userId);
    } finally {
      setMarkingAll(false);
    }
  }, [markingAll, queryClient, userId]);

  return { open, markAll, markingAll };
};
